import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { formatPhoneNumber } from "@tCooneyBrothers/Common/Utilities/formatPhoneNumber";
import {
    CallbackType,
    ErrorObjectType,
    InputObjectType,
    validate,
    validateConfirmPasswordHandler,
    validatePasswordHandler,
} from "@tCooneyBrothers/Common/Utilities/validate";
import {
    existingCustomerValidatorConfig,
    FormErrorsStateType,
    intialErrorFormState,
    ValidatorConfigType,
} from "@tCooneyBrothers/Components/CreateAccountExistingFormValidatorConfig";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    customModalName = "customModalName",
}

interface MessageFilterStyles {
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioButton?: FieldSetPresentationProps<RadioComponentProps>;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.customModalName]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    customModalName: state.components.contactUsForm.fieldValues["customModalName"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface EnrollFormStyles {
    accountNumberTextField?: TextFieldPresentationProps;
    messageFilterGridItem?: GridItemProps;
    messageFilter?: MessageFilterStyles;
    emailTextField?: TextFieldPresentationProps;
    userNameTextField?: TextFieldPresentationProps;
    label?: InjectableCss;
    radioWrapper?: InjectableCss;
}

export const enrollFormStyles: EnrollFormStyles = {
    messageFilterGridItem: { width: 12 },
    messageFilter: {
        radioGroup: {
            css: css`
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
            `,
        },
        radioButton: {
            css: css`
                margin-top: 10px;
            `,
        },
    },

    label: {
        css: css`
            min-height: 22px;
            display: inline-block;
            width: 100%;
            font-size: 15px;
            font-weight: 600;
        `,
    },
    radioWrapper: {
        css: css`
            padding-top: 15px;
            > div[class^="RadioGroupStyle"] {
                justify-content: space-between;
            }
            > div > div[class^="FormFieldStyle"] {
                padding-top: 15px;
            }
        `,
    },
};

const styles = enrollFormStyles;
const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
const emailFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");
const componentStyles: MessageFilterStyles = styles.messageFilter || {};

const EnrollmentForm: React.FC<Props> = () => {
    const [enrollType, setEnroll] = React.useState("");
    const [firstName, setFirstName] = React.useState("");
    const [lastName, setLastName] = React.useState("");
    const [phone, setPhone] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [emailError, setEmailError] = React.useState(emailRequiredFieldMessage);
    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormState);

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const enrollHandler = (enrollType: string) => {
        setEnroll(enrollType);
    };

    const emailChangeHandler = (input: HTMLInputElement) => {
        validateEmail(input.value);
        setEmail(input.value);
        validateHandler(input);
    };
    const firstNameChangeHandler = (input: HTMLInputElement) => {
        setFirstName(input.value);
        validateHandler(input);
    };
    const lastNameChangeHandler = (input: HTMLInputElement) => {
        setLastName(input.value);
        validateHandler(input);
    };
    const phoneChangeHandler = (input: HTMLInputElement) => {
        setPhone(formatPhoneNumber(input.value));
        validateHandler(input);
    };

    const validateHandler = (input: HTMLInputElement | HTMLSelectElement, customValidation?: CallbackType): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = existingCustomerValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }
    };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.radioWrapper}>
                <Typography {...styles.label}> Enrollment Type: </Typography>
                <RadioGroup
                    {...componentStyles.radioGroup}
                    value={enrollType}
                    onChangeHandler={(event: any) => enrollHandler(event.target.value)}
                >
                    <Radio {...componentStyles.radioButton} key="enrollingSelf">
                        {translate("Self")}
                    </Radio>
                    <Radio {...componentStyles.radioButton} key="enrollingSomeoneElse">
                        {translate("Someone Else")}
                    </Radio>
                    <Radio {...componentStyles.radioButton} key="enrollingBoth">
                        {translate("Both")}
                    </Radio>
                </RadioGroup>
                <StyledWrapper
                    style={{ display: enrollType === "Someone Else" || enrollType === "Both" ? "block" : "none" }}
                >
                    <TextField
                        {...styles.userNameTextField}
                        label={translate("First Name")}
                        value={firstName}
                        data-test-selector="createAccount_firstName"
                        onChange={e => firstNameChangeHandler(e.currentTarget)}
                        onBlur={e => validateHandler(e.currentTarget)}
                        error={!!formErrors.firstName && formErrors.firstName}
                        name="firstName"
                        iconProps={{ color: "danger", src: formErrors.firstName ? AlertTriangle : undefined }}
                        required
                    />
                    <TextField
                        {...styles.userNameTextField}
                        label={translate("Last Name")}
                        value={lastName}
                        data-test-selector="createAccount_lastName"
                        onChange={e => lastNameChangeHandler(e.currentTarget)}
                        onBlur={e => validateHandler(e.currentTarget)}
                        error={!!formErrors.lastName && formErrors.lastName}
                        name="lastName"
                        iconProps={{ color: "danger", src: formErrors.lastName ? AlertTriangle : undefined }}
                        required
                    />
                    <TextField
                        {...styles.userNameTextField}
                        label={translate("Phone")}
                        value={phone}
                        data-test-selector="createAccount_phone"
                        onChange={e => phoneChangeHandler(e.currentTarget)}
                        onBlur={e => validateHandler(e.currentTarget)}
                        error={!!formErrors.phone && formErrors.phone}
                        name="phone"
                        iconProps={{ color: "danger", src: formErrors.phone ? AlertTriangle : undefined }}
                        required
                    />
                    <TextField
                        {...styles.emailTextField}
                        type="email"
                        label={translate("Email Address")}
                        onChange={e => emailChangeHandler(e.currentTarget)}
                        value={email}
                        data-test-selector="createAccount_email"
                        required
                        onBlur={e => validateHandler(e.currentTarget)}
                        error={!!formErrors.email && formErrors.email}
                        name="email"
                        iconProps={{ color: "danger", src: formErrors.email ? AlertTriangle : undefined }}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EnrollmentForm),
    definition: {
        group: "Contact Us",
        fieldDefinitions: [],
    },
};

export default widgetModule;
